import { useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Pencil, X } from 'lucide-react';
import { usuarioService } from '@/services/api';
import { toast } from 'sonner';

const getIniciais = (nome: string): string => {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0][0] ?? '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] ?? '' : '';
  return (primeira + ultima).toUpperCase();
};

interface AvatarUploadProps {
  usuarioId: string;
  nome: string;
  avatarUrl: string | null | undefined;
  onChange: (avatarUrl: string | null) => void;
  size?: 'md' | 'lg';
}

export function AvatarUpload({ usuarioId, nome, avatarUrl, onChange, size = 'md' }: AvatarUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const dimensao = size === 'lg' ? 'h-20 w-20' : 'h-10 w-10';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setLoading(true);
    const response = await usuarioService.uploadAvatar(usuarioId, file);
    if (response.success) {
      onChange(response.data?.avatar_url ?? null);
      toast.success('Foto atualizada com sucesso!');
    } else {
      toast.error(response.error || 'Erro ao enviar foto');
    }
    setLoading(false);
  };

  const handleRemove = async () => {
    setLoading(true);
    const response = await usuarioService.removeAvatar(usuarioId);
    if (response.success) {
      onChange(null);
      toast.success('Foto removida');
    } else {
      toast.error(response.error || 'Erro ao remover foto');
    }
    setLoading(false);
  };

  return (
    <div className="group relative shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />
      <Avatar className={dimensao}>
        <AvatarImage src={avatarUrl ?? undefined} alt={nome} />
        <AvatarFallback>{getIniciais(nome)}</AvatarFallback>
      </Avatar>
      <button
        type="button"
        disabled={loading}
        onClick={() => inputRef.current?.click()}
        className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed"
        aria-label="Alterar foto"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Pencil className="h-5 w-5" />}
      </button>
      {avatarUrl && (
        <button
          type="button"
          disabled={loading}
          onClick={handleRemove}
          className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-destructive group-hover:opacity-100"
          aria-label="Remover foto"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
